import { isMarketplaceOrder } from './marketplaceOrder';

/**
 * The Online Service Fee on one order, in DOLLARS like every other order total.
 * Spec: docs/superpowers/specs/2026-10-05-online-service-fee-design.md §2.
 */
export interface OnlineServiceFeeLine {
  /** The fee before tax. */
  readonly amount: number;
  /** Tax on the fee — 0 when the location does not tax it. */
  readonly tax: number;
  /** The guest-facing label (restaurant may rename; default "Online Service Fee"). */
  readonly label: string;
}

/** `ICheckServiceCharge.id` of the fee's entry on the order's default check. */
export const ONLINE_SERVICE_FEE_CHARGE_ID = 'online_service_fee';

/**
 * The tenant flag that turns the Online Service Fee on (fail CLOSED). Defined
 * ONCE here — the API, management and billing (Part B) all import it; nobody
 * spells the string again.
 */
export const ONLINE_SERVICE_FEE_FLAG = 'initiative.online_service_fee';

/**
 * Was this order placed through OUR checkout (`POST /orders/checkout`), so the
 * Online Service Fee applies to the guest and OrderUp's per-order fee to the
 * restaurant? Online and never a marketplace: a DoorDash Drive order is ours
 * (DoorDash only couriers it). The ONE predicate — checkout and billing alike.
 */
export function isOnlineServiceFeeOrder(order: {
  readonly orderSource?: string | null;
  readonly externalSource?: string | null;
}): boolean {
  return (order.orderSource ?? '').toLowerCase() === 'online' && !isMarketplaceOrder(order);
}

/**
 * SERVER-OWNED stamp on an order placed under OrderUp's per-order fee program
 * (Part B). Checkout (`POST /orders/checkout`) writes it when the platform kill
 * switch `initiative.online_service_fee` is ON for an
 * {@link isOnlineServiceFeeOrder} order — whether or not the restaurant's own
 * guest fee is on or $0, because OrderUp bills the restaurant either way.
 *
 * Billing follows the CHECKOUT, never the clock at close: the ingest records an
 * order only when it carries this stamp, so an order placed while the program
 * was off is never billed when it closes after the switch flips on (a scheduled
 * order, or a close forward still retrying). Firestore rules refuse every
 * client write that sets, changes or removes it, and the offline projection
 * writers drop it (`SERVER_OWNED_ORDER_FIELDS`). Docs: docs/ONLINE_SERVICE_FEE.md.
 */
export interface OnlineOrderFeeProgramStamp {
  /** When checkout placed the order under the program. */
  readonly appliedAt: Date;
}

/** The stored field name, spelled once. */
export const ONLINE_ORDER_FEE_PROGRAM_FIELD = 'onlineOrderFeeProgram';

function toDateOrNull(v: unknown): Date | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const maybe = v as { toDate?: () => Date } | null | undefined;
  if (maybe && typeof maybe.toDate === 'function') {
    const d = maybe.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * A stored stamp (Date, Firestore Timestamp or ISO string `appliedAt`), or
 * undefined when absent or unreadable. Never throws.
 */
export function parseOnlineOrderFeeProgram(v: unknown): OnlineOrderFeeProgramStamp | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const appliedAt = toDateOrNull((v as { appliedAt?: unknown }).appliedAt);
  return appliedAt ? { appliedAt } : undefined;
}

/**
 * Was this order placed under OrderUp's per-order fee program? TRUE only for a
 * readable stamp: an order with none (placed before the program, or while the
 * switch was off) is never billed. The ONE predicate the ledger decides on.
 */
export function isInOnlineOrderFeeProgram(order: { readonly onlineOrderFeeProgram?: unknown }): boolean {
  return parseOnlineOrderFeeProgram(order.onlineOrderFeeProgram) !== undefined;
}
